import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  FileText,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  LogOut,
  Plus,
  ArrowLeft,
  Loader2,
  FileWarning,
  Shield
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage, translations } from "../context/LanguageContext";
import { MFASetupPanel } from "../components/MFASetupPanel";
import { useSessionTimeout } from "../hooks/useSessionTimeout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  active: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const PartnerDashboard = () => {
  const { language } = useLanguage();
  const t = translations[language].partnerDashboard;
  const tf = translations[language].forms;
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const handleSessionTimeout = useCallback(() => {
    toast.error("Session expired due to inactivity. Please log in again.");
    navigate("/partner/login");
  }, [navigate]);

  const handleSessionWarning = useCallback(() => {
    toast.warning("Your session will expire in 2 minutes due to inactivity.");
  }, []);

  useSessionTimeout(handleSessionTimeout, handleSessionWarning, !!partner);
  const [referrals, setReferrals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("referrals");
  const [lapReports, setLapReports] = useState([]);
  const [showLapForm, setShowLapForm] = useState(false);
  const [lapForm, setLapForm] = useState({
    victim_name: "", victim_phone: "", victim_email: "", victim_age: "",
    victim_gender: "female", victim_address: "", abuser_name: "",
    abuser_relationship: "", incident_date: "", incident_location: "",
    incident_description: "", risk_level: "high", lethality_score: "",
    officer_name: "", officer_badge: "", department_name: "", department_phone: "",
    children_involved: 0, weapons_involved: false, prior_incidents: false,
    protective_order: false, immediate_needs: [], notes: "",
  });

  const [formData, setFormData] = useState({
    name: "", organization: "", organization_type: "", client_type: "",
    email: "", phone: "", notes: ""
  });

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/stats`, { withCredentials: true });
      setStats(res.data);
    } catch {
      // silently fail
    }
  }, []);

  const fetchReferrals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/referrals`, { withCredentials: true });
      setReferrals(res.data);
    } catch {
      // silently fail
    }
  }, []);

  const fetchLapReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/lap-reports`, { withCredentials: true });
      setLapReports(res.data);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/partner/me`, { withCredentials: true });
        setPartner(res.data);
        await Promise.all([fetchStats(), fetchReferrals(), fetchLapReports()]);
      } catch {
        navigate("/partner/login");
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate, fetchStats, fetchReferrals, fetchLapReports]);

  const handleLogout = async () => {
    await axios.post(`${API}/partner/logout`, {}, { withCredentials: true });
    navigate("/partner/login");
  };

  const handleSubmitReferral = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/partner/referrals`, formData, { withCredentials: true });
      toast.success(t.referralSuccess);
      setFormData({ name: "", organization: "", organization_type: "", client_type: "", email: "", phone: "", notes: "" });
      setShowForm(false);
      await Promise.all([fetchStats(), fetchReferrals()]);
    } catch {
      toast.error(t.referralError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitLap = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...lapForm,
        victim_age: lapForm.victim_age ? parseInt(lapForm.victim_age) : null,
        lethality_score: lapForm.lethality_score ? parseInt(lapForm.lethality_score) : null,
        children_involved: parseInt(lapForm.children_involved) || 0,
      };
      await axios.post(`${API}/partner/lap-reports`, payload, { withCredentials: true });
      toast.success("LAP report submitted successfully");
      setShowLapForm(false);
      setLapForm({
        victim_name: "", victim_phone: "", victim_email: "", victim_age: "",
        victim_gender: "female", victim_address: "", abuser_name: "",
        abuser_relationship: "", incident_date: "", incident_location: "",
        incident_description: "", risk_level: "high", lethality_score: "",
        officer_name: "", officer_badge: "", department_name: "", department_phone: "",
        children_involved: 0, weapons_involved: false, prior_incidents: false,
        protective_order: false, immediate_needs: [], notes: "",
      });
      fetchLapReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit LAP report");
    } finally {
      setIsSubmitting(false);
    }
  };

  const NEED_OPTIONS = ["Emergency Shelter", "Safety Planning", "Counseling", "Legal Aid", "Medical", "Housing", "Financial", "Childcare", "Transportation"];
  const toggleNeed = (need) => {
    setLapForm((prev) => ({
      ...prev,
      immediate_needs: prev.immediate_needs.includes(need)
        ? prev.immediate_needs.filter((n) => n !== need)
        : [...prev.immediate_needs, need],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-warm-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-gray-600">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }} data-testid="partner-dashboard-title">
                {t.title}
              </h1>
              <p className="text-sm text-gray-500">
                {partner?.name} — {partner?.organization}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="rounded-full gap-2"
            data-testid="partner-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {t.logout}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: t.totalReferrals, value: stats.total_referrals, icon: FileText, color: "bg-warm-100 text-warm-700" },
              { label: t.pending, value: stats.pending, icon: Clock, color: "bg-yellow-100 text-yellow-700" },
              { label: t.active, value: stats.active, icon: Users, color: "bg-blue-100 text-blue-700" },
              { label: t.completed, value: stats.completed, icon: CheckCircle, color: "bg-green-100 text-green-700" },
            ].map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-white rounded-xl p-5 border border-gray-100"
                data-testid={`partner-stat-${stat.label}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">{stat.label}</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            size="sm"
            variant={activeTab === "referrals" ? "default" : "outline"}
            className={activeTab === "referrals" ? "bg-warm-700 text-white" : ""}
            onClick={() => setActiveTab("referrals")}
            data-testid="partner-tab-referrals"
          >
            <FileText className="w-4 h-4 mr-1" /> Referrals ({referrals.length})
          </Button>
          <Button
            size="sm"
            variant={activeTab === "lap" ? "default" : "outline"}
            className={activeTab === "lap" ? "bg-warm-700 text-white" : ""}
            onClick={() => setActiveTab("lap")}
            data-testid="partner-tab-lap"
          >
            <FileWarning className="w-4 h-4 mr-1" /> LAP Reports ({lapReports.length})
          </Button>
          <Button
            size="sm"
            variant={activeTab === "security" ? "default" : "outline"}
            className={activeTab === "security" ? "bg-warm-700 text-white" : ""}
            onClick={() => setActiveTab("security")}
            data-testid="partner-tab-security"
          >
            <Shield className="w-4 h-4 mr-1" /> Security
          </Button>
        </div>

        {activeTab === "referrals" && (<>
        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            {t.myReferrals}
          </h2>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-warm-700 hover:bg-warm-800 text-white rounded-full gap-2"
            data-testid="partner-new-referral-btn"
          >
            <Plus className="w-4 h-4" />
            {t.newReferral}
          </Button>
        </div>

        {/* Referral Form */}
        {showForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            onSubmit={handleSubmitReferral}
            className="bg-white rounded-2xl p-6 border border-gray-100 mb-6 space-y-4"
          >
            <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              {t.submitReferral}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">{t.contactName} *</Label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl border-gray-200" data-testid="partner-ref-name" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">{t.orgName} *</Label>
                <Input required value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="rounded-xl border-gray-200" data-testid="partner-ref-org" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">{t.orgType} *</Label>
                <Select value={formData.organization_type} onValueChange={(v) => setFormData({ ...formData, organization_type: v })}>
                  <SelectTrigger className="rounded-xl border-gray-200" data-testid="partner-ref-orgtype">
                    <SelectValue placeholder={tf.selectOption} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cps">CPS/Child Welfare</SelectItem>
                    <SelectItem value="court">Court/Legal</SelectItem>
                    <SelectItem value="hospital">Hospital/Healthcare</SelectItem>
                    <SelectItem value="nonprofit">Nonprofit</SelectItem>
                    <SelectItem value="attorney">Attorney</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">{t.clientNeeds} *</Label>
                <Select value={formData.client_type} onValueChange={(v) => setFormData({ ...formData, client_type: v })}>
                  <SelectTrigger className="rounded-xl border-gray-200" data-testid="partner-ref-clienttype">
                    <SelectValue placeholder={tf.selectOption} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parenting-classes">Parenting Classes</SelectItem>
                    <SelectItem value="violence-intervention">Violence Intervention</SelectItem>
                    <SelectItem value="family-support">Family Support</SelectItem>
                    <SelectItem value="multiple">Multiple Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">{t.clientEmail} *</Label>
                <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-xl border-gray-200" data-testid="partner-ref-email" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">{t.clientPhone} *</Label>
                <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-xl border-gray-200" data-testid="partner-ref-phone" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-1 block">{t.notes}</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-xl border-gray-200 min-h-[80px]" data-testid="partner-ref-notes" />
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={isSubmitting} className="bg-warm-700 hover:bg-warm-800 text-white rounded-full" data-testid="partner-ref-submit">
                {isSubmitting ? t.submitting : t.submit}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-full">
                {t.cancel}
              </Button>
            </div>
          </motion.form>
        )}

        {/* Referrals Table */}
        {referrals.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border border-gray-100 text-center">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">{t.noReferrals}</p>
            <p className="text-sm text-gray-500">{t.noReferralsNote}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" role="table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase" scope="col">{t.client}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase" scope="col">{t.service}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase" scope="col">{t.status}</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase" scope="col">{t.date}</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((ref) => (
                    <tr key={ref.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" data-testid={`partner-referral-${ref.id}`}>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900 text-sm">{ref.name}</p>
                        <p className="text-xs text-gray-500">{ref.email}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{ref.client_type}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusColors[ref.status] || 'bg-gray-100 text-gray-800'}`}>
                          {ref.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(ref.created_at).toLocaleDateString(language === 'es' ? 'es-US' : 'en-US')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>)}

        {/* LAP Reports Tab */}
        {activeTab === "lap" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                LAP Reports
              </h2>
              <Button
                onClick={() => setShowLapForm(!showLapForm)}
                className="bg-warm-700 hover:bg-warm-800 text-white rounded-full gap-2"
                data-testid="partner-new-lap-btn"
              >
                <Plus className="w-4 h-4" />
                Submit LAP Report
              </Button>
            </div>

            {showLapForm && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                onSubmit={handleSubmitLap}
                className="bg-white rounded-2xl p-6 border border-gray-100 mb-6 space-y-4"
                data-testid="lap-report-form"
              >
                <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                  Lethality Assessment Program Report
                </h3>

                {/* Victim Info */}
                <p className="text-xs uppercase font-semibold text-gray-500 tracking-wider">Victim Information</p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Victim Name *</Label>
                    <Input required value={lapForm.victim_name} onChange={(e) => setLapForm({ ...lapForm, victim_name: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Phone</Label>
                    <Input value={lapForm.victim_phone} onChange={(e) => setLapForm({ ...lapForm, victim_phone: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Email</Label>
                    <Input type="email" value={lapForm.victim_email} onChange={(e) => setLapForm({ ...lapForm, victim_email: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Age</Label>
                    <Input type="number" value={lapForm.victim_age} onChange={(e) => setLapForm({ ...lapForm, victim_age: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Gender</Label>
                    <Select value={lapForm.victim_gender} onValueChange={(v) => setLapForm({ ...lapForm, victim_gender: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Address</Label>
                    <Input value={lapForm.victim_address} onChange={(e) => setLapForm({ ...lapForm, victim_address: e.target.value })} className="rounded-xl" />
                  </div>
                </div>

                {/* Abuser & Incident */}
                <p className="text-xs uppercase font-semibold text-gray-500 tracking-wider pt-2">Incident Details</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Abuser Name</Label>
                    <Input value={lapForm.abuser_name} onChange={(e) => setLapForm({ ...lapForm, abuser_name: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Relationship</Label>
                    <Input value={lapForm.abuser_relationship} onChange={(e) => setLapForm({ ...lapForm, abuser_relationship: e.target.value })} placeholder="e.g. Partner, Spouse" className="rounded-xl" />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Incident Date *</Label>
                    <Input type="date" required value={lapForm.incident_date} onChange={(e) => setLapForm({ ...lapForm, incident_date: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Location</Label>
                    <Input value={lapForm.incident_location} onChange={(e) => setLapForm({ ...lapForm, incident_location: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Risk Level *</Label>
                    <Select value={lapForm.risk_level} onValueChange={(v) => setLapForm({ ...lapForm, risk_level: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="danger">Danger</SelectItem>
                        <SelectItem value="extreme-danger">Extreme Danger</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Incident Description</Label>
                  <Textarea value={lapForm.incident_description} onChange={(e) => setLapForm({ ...lapForm, incident_description: e.target.value })} className="rounded-xl min-h-[80px]" />
                </div>

                {/* Assessment */}
                <p className="text-xs uppercase font-semibold text-gray-500 tracking-wider pt-2">Assessment</p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Lethality Score</Label>
                    <Input type="number" value={lapForm.lethality_score} onChange={(e) => setLapForm({ ...lapForm, lethality_score: e.target.value })} className="rounded-xl" placeholder="0-20" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Children Involved</Label>
                    <Input type="number" value={lapForm.children_involved} onChange={(e) => setLapForm({ ...lapForm, children_involved: parseInt(e.target.value) || 0 })} className="rounded-xl" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={lapForm.weapons_involved} onChange={(e) => setLapForm({ ...lapForm, weapons_involved: e.target.checked })} />
                    Weapons Involved
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={lapForm.prior_incidents} onChange={(e) => setLapForm({ ...lapForm, prior_incidents: e.target.checked })} />
                    Prior Incidents
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={lapForm.protective_order} onChange={(e) => setLapForm({ ...lapForm, protective_order: e.target.checked })} />
                    Protective Order
                  </label>
                </div>

                {/* Immediate Needs */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">Immediate Needs</Label>
                  <div className="flex flex-wrap gap-2">
                    {NEED_OPTIONS.map((need) => (
                      <button
                        key={need}
                        type="button"
                        onClick={() => toggleNeed(need)}
                        className={`px-3 py-1 rounded-full text-xs border ${
                          lapForm.immediate_needs.includes(need)
                            ? "bg-warm-100 border-warm-400 text-warm-800"
                            : "bg-white border-gray-200 text-gray-600"
                        }`}
                      >
                        {need}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Officer Info */}
                <p className="text-xs uppercase font-semibold text-gray-500 tracking-wider pt-2">Submitting Officer</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Officer Name</Label>
                    <Input value={lapForm.officer_name} onChange={(e) => setLapForm({ ...lapForm, officer_name: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Badge #</Label>
                    <Input value={lapForm.officer_badge} onChange={(e) => setLapForm({ ...lapForm, officer_badge: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Department</Label>
                    <Input value={lapForm.department_name} onChange={(e) => setLapForm({ ...lapForm, department_name: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">Department Phone</Label>
                    <Input value={lapForm.department_phone} onChange={(e) => setLapForm({ ...lapForm, department_phone: e.target.value })} className="rounded-xl" />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Additional Notes</Label>
                  <Textarea value={lapForm.notes} onChange={(e) => setLapForm({ ...lapForm, notes: e.target.value })} className="rounded-xl" />
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={isSubmitting} className="bg-warm-700 hover:bg-warm-800 text-white rounded-full" data-testid="partner-lap-submit">
                    {isSubmitting ? "Submitting..." : "Submit LAP Report"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowLapForm(false)} className="rounded-full">Cancel</Button>
                </div>
              </motion.form>
            )}

            {/* LAP Reports List */}
            {lapReports.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 border border-gray-100 text-center">
                <FileWarning className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No LAP reports submitted yet</p>
                <p className="text-sm text-gray-500">Submit a Lethality Assessment Program report to get started.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Victim</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Risk</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Incident Date</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lapReports.map((r) => (
                        <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`partner-lap-${r.id}`}>
                          <td className="px-6 py-4">
                            <p className="font-medium text-gray-900 text-sm">{r.victim_name}</p>
                            <p className="text-xs text-gray-500">{r.victim_phone}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              r.risk_level === "extreme-danger" ? "bg-red-200 text-red-900" :
                              r.risk_level === "danger" ? "bg-red-100 text-red-800" : "bg-orange-100 text-orange-800"
                            }`}>{r.risk_level?.replace("-", " ")}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{r.incident_date}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              r.status === "received" ? "bg-yellow-100 text-yellow-800" :
                              r.status === "resources_assigned" ? "bg-green-100 text-green-800" :
                              r.status === "closed" ? "bg-gray-100 text-gray-600" : "bg-blue-100 text-blue-800"
                            }`}>{r.status?.replace("_", " ")}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Security Tab */}
        {activeTab === "security" && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <MFASetupPanel />
          </div>
        )}
      </main>
    </div>
  );
};

export default PartnerDashboard;
